import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createPositecClient,
  discoverConnection,
  type AuthTenant,
  type AuthUser,
  type CashStatusLocal,
  type CatalogPaymentMethod,
  type CatalogProduct,
  type CatalogSalesCenter,
  type CatalogSalesLocation,
  type CatalogSnapshot,
  type ConnectionMode,
  type DiscoveryResult,
  type PositecClient,
  type Ticket,
} from '@positec/api-client';
import { loadCatalogFromCache, saveCatalogToCache } from './db';

const TOKEN_KEY = 'positec_pos_token';
const CLOUD_URL = import.meta.env.VITE_API_URL ?? 'https://api.itpos.itrionsoft.com';

type Session = { user: AuthUser; tenant: AuthTenant | null };

type View =
  | { kind: 'login' }
  | { kind: 'centers' }
  | { kind: 'tables'; center: CatalogSalesCenter }
  | { kind: 'sale'; center: CatalogSalesCenter; location: CatalogSalesLocation | null; ticket: Ticket }
  | { kind: 'payment'; center: CatalogSalesCenter; location: CatalogSalesLocation | null; ticket: Ticket };

function buildClient(baseURL: string): PositecClient {
  return createPositecClient({
    baseURL,
    getToken: () => localStorage.getItem(TOKEN_KEY),
    onUnauthenticated: () => localStorage.removeItem(TOKEN_KEY),
  });
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [snapshot, setSnapshot] = useState<CatalogSnapshot | null>(null);
  const [view, setView] = useState<View>({ kind: 'login' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [discovery, setDiscovery] = useState<DiscoveryResult>({
    baseURL: CLOUD_URL,
    mode: 'cloud',
  });
  const [client, setClient] = useState<PositecClient>(() => buildClient(CLOUD_URL));

  // Bootstrap: descubrir Bridge → hidratar sesión + catálogo (con fallback a IndexedDB)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const disc = await discoverConnection({ cloudURL: CLOUD_URL });
      if (cancelled) return;
      setDiscovery(disc);
      const c = buildClient(disc.baseURL);
      setClient(c);

      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setLoading(false);
        return;
      }

      // Intentamos llamar al API. Si falla, miramos cache local.
      try {
        const meRes = await c.me();
        if (cancelled) return;
        setSession({ user: meRes.user, tenant: meRes.tenant });

        try {
          const snap = await c.catalogSnapshot();
          if (cancelled) return;
          setSnapshot(snap);
          // Cachear en IndexedDB para próxima vez sin red
          saveCatalogToCache(snap, disc.baseURL).catch(() => {});
        } catch {
          // Cloud/Bridge no responde — intentar cache
          const cached = await loadCatalogFromCache();
          if (cached && !cancelled) {
            setSnapshot(cached.snapshot);
            setError(`Trabajando en modo degradado: catálogo de ${new Date(cached.fetched_at).toLocaleString('es-ES')}.`);
          } else if (!cancelled) {
            setError('No hay catálogo disponible (sin red ni cache).');
          }
        }

        setView({ kind: 'centers' });
      } catch {
        // me() falló — token inválido o sin red.
        // Si hay cache de catálogo, podríamos mostrar UI degradada,
        // pero sin sesión válida no tiene sentido. Limpiamos.
        localStorage.removeItem(TOKEN_KEY);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <Splash text="Cargando ITpos TPV…" />;
  }

  if (view.kind === 'login' || session === null || snapshot === null) {
    return (
      <Splash connection={discovery}>
        <LoginForm
          client={client}
          onSuccess={async (s) => {
            setSession(s);
            try {
              const snap = await client.catalogSnapshot();
              setSnapshot(snap);
              saveCatalogToCache(snap, discovery.baseURL).catch(() => {});
              setView({ kind: 'centers' });
            } catch (e) {
              const cached = await loadCatalogFromCache();
              if (cached) {
                setSnapshot(cached.snapshot);
                setError(
                  `Trabajando con catálogo cacheado de ${new Date(cached.fetched_at).toLocaleString('es-ES')}.`,
                );
                setView({ kind: 'centers' });
              } else {
                setError(e instanceof Error ? e.message : 'Error cargando catálogo');
              }
            }
          }}
        />
        {error && <p className="text-sm text-red-300">{error}</p>}
      </Splash>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Header
        session={session}
        view={view}
        connection={discovery}
        onBack={() => {
          if (view.kind === 'tables') setView({ kind: 'centers' });
          else if (view.kind === 'sale') setView({ kind: 'tables', center: view.center });
          else if (view.kind === 'payment') setView({ kind: 'sale', center: view.center, location: view.location, ticket: view.ticket });
        }}
        onLogout={() => {
          client.logout().finally(() => {
            localStorage.removeItem(TOKEN_KEY);
            setSession(null);
            setSnapshot(null);
            setView({ kind: 'login' });
          });
        }}
      />
      <main className="flex flex-1 overflow-hidden">
        {view.kind === 'centers' && (
          <CentersView
            snapshot={snapshot}
            onSelect={(center) => setView({ kind: 'tables', center })}
          />
        )}
        {view.kind === 'tables' && (
          <TablesView
            center={view.center}
            onSelect={async (location) => {
              try {
                let ticket = await client.ticketByLocation(location.id);
                if (ticket === null) {
                  ticket = await client.createTicket({
                    sales_center_id: view.center.id,
                    sales_location_id: location.id,
                  });
                }
                setView({ kind: 'sale', center: view.center, location, ticket });
              } catch (e) {
                setError(e instanceof Error ? e.message : 'Error abriendo mesa');
              }
            }}
          />
        )}
        {view.kind === 'sale' && (
          <SaleView
            client={client}
            snapshot={snapshot}
            ticket={view.ticket}
            onTicketUpdate={(t) => setView({ ...view, ticket: t })}
            onCobrar={() => setView({ kind: 'payment', center: view.center, location: view.location, ticket: view.ticket })}
            onClose={async () => {
              setView({ kind: 'tables', center: view.center });
            }}
            onCancel={async () => {
              if (!confirm('¿Cancelar este ticket por completo?')) return;
              await client.cancelTicket(view.ticket.id);
              setView({ kind: 'tables', center: view.center });
            }}
          />
        )}
        {view.kind === 'payment' && (
          <PaymentView
            client={client}
            snapshot={snapshot}
            ticket={view.ticket}
            onPaid={(updated) => {
              if (updated.status === 'closed') {
                client.me().then((r) => setSession({ user: r.user, tenant: r.tenant }));
                setView({ kind: 'tables', center: view.center });
              } else {
                setView({ ...view, ticket: updated });
              }
            }}
            onCancel={() => setView({ kind: 'sale', center: view.center, location: view.location, ticket: view.ticket })}
          />
        )}
      </main>
    </div>
  );
}

// =====================================================================
//  Header
// =====================================================================
function Header({
  session,
  view,
  connection,
  onBack,
  onLogout,
}: {
  session: Session;
  view: View;
  connection: DiscoveryResult;
  onBack: () => void;
  onLogout: () => void;
}) {
  const showBack = view.kind === 'tables' || view.kind === 'sale' || view.kind === 'payment';

  let title = 'ITpos TPV';
  if (view.kind === 'tables') title = view.center.name;
  if (view.kind === 'sale') title = `${view.center.name} · ${view.location?.name ?? '-'}`;
  if (view.kind === 'payment') title = `Cobro · ${view.location?.name ?? '-'}`;

  return (
    <header className="flex items-center justify-between border-b border-slate-800 bg-slate-900 px-4 py-2">
      <div className="flex items-center gap-3">
        {showBack && (
          <button
            onClick={onBack}
            className="rounded border border-slate-700 px-3 py-2 hover:bg-slate-800"
            aria-label="Atrás"
          >
            ←
          </button>
        )}
        <div className="h-7 w-7 rounded bg-positec-accent" />
        <span className="text-base font-semibold">{title}</span>
        <span className="rounded bg-slate-800 px-2 py-0.5 text-xs text-slate-400">DESA</span>
        <ConnectionBadge mode={connection.mode} bridgeName={connection.bridge?.bridge_name} />
      </div>
      <div className="flex items-center gap-3 text-sm">
        {session.tenant && (
          <span>
            <span className="text-slate-400">{session.tenant.name}</span> ·{' '}
            <span className="font-mono text-positec-accent">
              {session.tenant.ticket_balance.toLocaleString('es-ES')}
            </span>
            <span className="text-slate-500"> tickets</span>
          </span>
        )}
        <span className="text-slate-300">{session.user.name}</span>
        <button
          onClick={onLogout}
          className="rounded border border-slate-700 px-3 py-1.5 hover:bg-slate-800"
        >
          Salir
        </button>
      </div>
    </header>
  );
}

function ConnectionBadge({
  mode,
  bridgeName,
}: {
  mode: ConnectionMode;
  bridgeName?: string;
}) {
  const cfg = {
    bridge: {
      label: 'Bridge local',
      color: 'bg-emerald-600/30 text-emerald-300 border-emerald-700/50',
      tooltip: bridgeName ? `Conectado al Bridge: ${bridgeName}` : 'Bridge local activo',
    },
    cloud: {
      label: 'Cloud directo',
      color: 'bg-amber-600/30 text-amber-200 border-amber-700/50',
      tooltip: 'Sin Bridge en LAN — hablando directo al cloud',
    },
    offline: {
      label: 'Offline',
      color: 'bg-red-700/30 text-red-200 border-red-700/50',
      tooltip: 'Sin conexión',
    },
  }[mode];

  return (
    <span
      title={cfg.tooltip}
      className={`rounded border px-2 py-0.5 text-xs ${cfg.color}`}
    >
      ● {cfg.label}
    </span>
  );
}

// =====================================================================
//  Splash + Login
// =====================================================================
function Splash({
  text,
  children,
  connection,
}: {
  text?: string;
  children?: React.ReactNode;
  connection?: DiscoveryResult;
}) {
  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-b from-slate-900 to-slate-950 p-6">
      <div className="flex max-w-md flex-col items-center gap-5 text-center">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded bg-positec-accent" />
          <h1 className="text-3xl font-semibold tracking-wide">ITpos TPV</h1>
        </div>
        {connection && (
          <ConnectionBadge mode={connection.mode} bridgeName={connection.bridge?.bridge_name} />
        )}
        {text && <p className="text-slate-400">{text}</p>}
        {children}
      </div>
    </div>
  );
}

function LoginForm({
  client,
  onSuccess,
}: {
  client: PositecClient;
  onSuccess: (s: Session) => void;
}) {
  const [email, setEmail] = useState('pepe@barpepe.demo');
  const [password, setPassword] = useState('demo1234');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await client.login({ email, password, device_name: 'pos-web' });
      localStorage.setItem(TOKEN_KEY, r.token);
      onSuccess({ user: r.user, tenant: r.tenant });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de login');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="w-full space-y-4 rounded-lg border border-slate-800 bg-slate-900/70 p-6 text-left shadow-2xl"
    >
      <h2 className="text-center text-lg">Acceso al punto de venta</h2>
      <input
        type="email"
        required
        autoFocus
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="email"
        className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-3 outline-none focus:border-positec-accent"
      />
      <input
        type="password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="contraseña"
        className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-3 outline-none focus:border-positec-accent"
      />
      {error && (
        <div className="rounded border border-red-700/50 bg-red-900/30 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="w-full rounded bg-positec-accent px-4 py-3 font-medium text-white transition hover:bg-orange-600 disabled:opacity-50"
      >
        {submitting ? 'Entrando…' : 'Acceder'}
      </button>
    </form>
  );
}

// =====================================================================
//  Vista: Centros de venta
// =====================================================================
function CentersView({
  snapshot,
  onSelect,
}: {
  snapshot: CatalogSnapshot;
  onSelect: (c: CatalogSalesCenter) => void;
}) {
  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="grid w-full max-w-2xl gap-4 sm:grid-cols-2 md:grid-cols-3">
        {snapshot.sales_centers.map((center) => (
          <button
            key={center.id}
            onClick={() => onSelect(center)}
            className="flex aspect-video flex-col items-center justify-center rounded-lg border border-slate-700 bg-slate-900/50 p-6 text-xl font-medium hover:border-positec-accent hover:bg-slate-800"
          >
            {center.name}
            <span className="mt-2 text-sm text-slate-400">
              {center.locations.length} ubicaciones
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
//  Vista: Mesas / Ubicaciones
// =====================================================================
function TablesView({
  center,
  onSelect,
}: {
  center: CatalogSalesCenter;
  onSelect: (l: CatalogSalesLocation) => void;
}) {
  return (
    <div className="flex flex-1 items-start justify-center overflow-auto p-6">
      <div className="grid w-full max-w-4xl grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
        {center.locations.map((loc) => (
          <button
            key={loc.id}
            onClick={() => onSelect(loc)}
            className="flex aspect-square flex-col items-center justify-center rounded-lg border border-slate-700 bg-slate-900/50 text-xl font-medium hover:border-positec-accent hover:bg-slate-800"
          >
            {loc.name}
            {loc.seats !== null && (
              <span className="mt-1 text-xs text-slate-500">{loc.seats} pax</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
//  Vista: Sale (catálogo + ticket)
// =====================================================================
function SaleView({
  client,
  snapshot,
  ticket,
  onTicketUpdate,
  onCobrar,
  onClose,
  onCancel,
}: {
  client: PositecClient;
  snapshot: CatalogSnapshot;
  ticket: Ticket;
  onTicketUpdate: (t: Ticket) => void;
  onCobrar: () => void;
  onClose: () => void;
  onCancel: () => void;
}) {
  const [activeFamilyId, setActiveFamilyId] = useState<string | null>(snapshot.families[0]?.id ?? null);
  const [busy, setBusy] = useState(false);

  const productsByFamily = useMemo(() => {
    return snapshot.products.filter((p) => p.family_id === activeFamilyId && p.sellable_as_main);
  }, [snapshot.products, activeFamilyId]);

  const addProduct = useCallback(async (product: CatalogProduct, formatId?: string) => {
    setBusy(true);
    try {
      const t = await client.addTicketLine(ticket.id, {
        product_id: product.id,
        format_id: formatId,
        quantity: 1,
      });
      onTicketUpdate(t);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error añadiendo línea');
    } finally {
      setBusy(false);
    }
  }, [client, ticket.id, onTicketUpdate]);

  const deleteLine = useCallback(async (lineId: string) => {
    setBusy(true);
    try {
      const t = await client.deleteTicketLine(ticket.id, lineId);
      onTicketUpdate(t);
    } finally {
      setBusy(false);
    }
  }, [client, ticket.id, onTicketUpdate]);

  const empty = (ticket.lines ?? []).length === 0;

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Catálogo */}
      <section className="flex flex-1 flex-col overflow-hidden border-r border-slate-800">
        {/* Familias */}
        <div className="flex flex-wrap gap-2 border-b border-slate-800 bg-slate-900/50 p-3">
          {snapshot.families.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFamilyId(f.id)}
              style={{
                backgroundColor: activeFamilyId === f.id ? f.button_color ?? '#1f2937' : undefined,
              }}
              className={`rounded px-3 py-2 text-sm font-medium transition ${
                activeFamilyId === f.id
                  ? 'text-white shadow'
                  : 'border border-slate-700 hover:bg-slate-800'
              }`}
            >
              {f.name}
            </button>
          ))}
        </div>
        {/* Productos */}
        <div className="grid flex-1 auto-rows-min grid-cols-2 gap-3 overflow-auto p-3 sm:grid-cols-3 md:grid-cols-4">
          {productsByFamily.map((p) => (
            <ProductButton key={p.id} product={p} onAdd={addProduct} disabled={busy} />
          ))}
          {productsByFamily.length === 0 && (
            <p className="col-span-full p-6 text-center text-slate-500">
              No hay productos en esta familia.
            </p>
          )}
        </div>
      </section>

      {/* Ticket */}
      <aside className="flex w-96 flex-col overflow-hidden bg-slate-900/30">
        <header className="border-b border-slate-800 px-4 py-3">
          <div className="text-xs uppercase tracking-wide text-slate-500">Ticket abierto</div>
          <div className="text-sm text-slate-300">
            {ticket.diners} comensal{ticket.diners === 1 ? '' : 'es'} · {ticket.status}
          </div>
        </header>

        <ul className="flex-1 overflow-auto px-2 py-2">
          {empty && (
            <li className="p-6 text-center text-sm text-slate-500">
              Toca un producto del catálogo para añadirlo al ticket.
            </li>
          )}
          {(ticket.lines ?? []).map((l) => (
            <li
              key={l.id}
              className="group flex items-center justify-between rounded px-2 py-2 hover:bg-slate-900/70"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-sm text-slate-400">
                    {parseFloat(l.quantity).toFixed(0)}×
                  </span>
                  <span className="truncate">{l.product_name_snapshot}</span>
                </div>
                {l.notes && <div className="ml-7 text-xs text-slate-500">{l.notes}</div>}
              </div>
              <div className="ml-3 flex items-baseline gap-2">
                <span className="font-mono text-sm">{parseFloat(l.total).toFixed(2)}€</span>
                <button
                  onClick={() => deleteLine(l.id)}
                  className="rounded px-2 py-0.5 text-xs text-slate-500 opacity-0 hover:bg-red-900/40 hover:text-red-300 group-hover:opacity-100"
                  aria-label="Quitar"
                >
                  ✕
                </button>
              </div>
            </li>
          ))}
        </ul>

        <footer className="space-y-3 border-t border-slate-800 p-4">
          <Totals ticket={ticket} />
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 rounded border border-slate-700 px-3 py-3 text-sm hover:bg-slate-800"
              disabled={busy}
            >
              Volver a mesas
            </button>
            <button
              onClick={onCancel}
              className="rounded border border-red-700/50 px-3 py-3 text-sm text-red-300 hover:bg-red-900/30"
              disabled={busy}
            >
              Cancelar
            </button>
            <button
              onClick={onCobrar}
              disabled={busy || empty}
              className="flex-[1.5] rounded bg-positec-accent px-3 py-3 font-medium text-white transition hover:bg-orange-600 disabled:opacity-40"
            >
              Cobrar {parseFloat(ticket.total).toFixed(2)}€
            </button>
          </div>
        </footer>
      </aside>
    </div>
  );
}

function ProductButton({
  product,
  onAdd,
  disabled,
}: {
  product: CatalogProduct;
  onAdd: (p: CatalogProduct, formatId?: string) => void;
  disabled: boolean;
}) {
  const basePrice = product.prices['__base__'];
  const hasFormats = product.formats.length > 0;

  if (hasFormats) {
    return (
      <div className="flex flex-col rounded-lg border border-slate-700 bg-slate-900/50 p-2">
        <div className="mb-2 truncate text-sm font-medium">{product.name}</div>
        <div className="grid flex-1 grid-cols-1 gap-1">
          {product.formats.map((f) => {
            const price = product.prices[f.id];
            return (
              <button
                key={f.id}
                disabled={disabled || price === undefined}
                onClick={() => onAdd(product, f.id)}
                className="flex items-center justify-between rounded bg-slate-800 px-2 py-1.5 text-sm hover:bg-slate-700 disabled:opacity-40"
              >
                <span>{f.name}</span>
                {price !== undefined && (
                  <span className="font-mono text-xs text-positec-accent">
                    {price.toFixed(2)}€
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => onAdd(product)}
      disabled={disabled}
      className="flex aspect-square flex-col items-center justify-center rounded-lg border border-slate-700 bg-slate-900/50 p-2 text-center hover:border-positec-accent hover:bg-slate-800 disabled:opacity-40"
    >
      <div className="line-clamp-3 text-sm font-medium">{product.name}</div>
      {basePrice !== undefined && (
        <div className="mt-2 font-mono text-sm text-positec-accent">{basePrice.toFixed(2)}€</div>
      )}
    </button>
  );
}

function Totals({ ticket }: { ticket: Ticket }) {
  return (
    <dl className="space-y-1 text-sm">
      <div className="flex justify-between text-slate-400">
        <dt>Subtotal</dt>
        <dd className="font-mono">{parseFloat(ticket.subtotal_net).toFixed(2)}€</dd>
      </div>
      <div className="flex justify-between text-slate-400">
        <dt>IVA</dt>
        <dd className="font-mono">{parseFloat(ticket.tax_total).toFixed(2)}€</dd>
      </div>
      <div className="flex justify-between border-t border-slate-700 pt-1 text-lg font-semibold">
        <dt>Total</dt>
        <dd className="font-mono text-positec-accent">{parseFloat(ticket.total).toFixed(2)}€</dd>
      </div>
      {parseFloat(ticket.paid) > 0 && (
        <div className="flex justify-between text-slate-400">
          <dt>Pagado</dt>
          <dd className="font-mono">{parseFloat(ticket.paid).toFixed(2)}€</dd>
        </div>
      )}
    </dl>
  );
}

// =====================================================================
//  Vista: Cobro
// =====================================================================
function PaymentView({
  client,
  snapshot,
  ticket,
  onPaid,
  onCancel,
}: {
  client: PositecClient;
  snapshot: CatalogSnapshot;
  ticket: Ticket;
  onPaid: (t: Ticket) => void;
  onCancel: () => void;
}) {
  const [selectedMethod, setSelectedMethod] = useState<CatalogPaymentMethod | null>(
    snapshot.payment_methods.find((m) => m.code === 'CASH') ?? snapshot.payment_methods[0] ?? null,
  );
  const balance = parseFloat(ticket.total) - parseFloat(ticket.paid);
  const [tendered, setTendered] = useState<string>(balance.toFixed(2));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenderedNum = Number.parseFloat(tendered) || 0;
  const change = selectedMethod?.returns_change
    ? Math.max(0, tenderedNum - balance)
    : 0;

  // Caja de efectivo (Fase 6): solo si estamos hablando con un Bridge local.
  const [cashStatus, setCashStatus] = useState<CashStatusLocal | null>(null);
  useEffect(() => {
    let alive = true;
    client.cashStatusLocal().then(
      (s) => { if (alive) setCashStatus(s); },
      () => { /* sin caja o sin bridge — silenciar */ },
    );
    return () => { alive = false; };
  }, [client]);
  const cashAvailable = !!cashStatus?.online && selectedMethod?.code === 'CASH';

  // Datáfono (Fase 11): solo si Bridge local detectado y método pago tarjeta.
  const [datafonoOnline, setDatafonoOnline] = useState(false);
  const [datafonoKind, setDatafonoKind] = useState<string>('');
  const [terminalId, setTerminalId] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    client.datafonoStatusLocal().then(
      (s) => { if (alive) { setDatafonoOnline(true); setDatafonoKind(s.config_kind); } },
      () => { /* sin datáfono o sin bridge */ },
    );
    client.listPaymentTerminals(true).then(
      (r) => { if (alive && r.terminals?.[0]) setTerminalId(r.terminals[0].id); },
      () => {},
    );
    return () => { alive = false; };
  }, [client]);
  const cardAvailable = datafonoOnline && terminalId && selectedMethod?.code === 'CARD';

  async function pay() {
    if (selectedMethod === null) return;
    setError(null);
    setSubmitting(true);
    try {
      const updated = await client.addTicketPayment(ticket.id, {
        payment_method_id: selectedMethod.id,
        amount: balance,
        tendered: selectedMethod.returns_change ? tenderedNum : balance,
      });
      onPaid(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error en el cobro');
    } finally {
      setSubmitting(false);
    }
  }

  async function payWithCashMachine() {
    if (selectedMethod === null || !cashAvailable) return;
    setError(null);
    setSubmitting(true);
    try {
      const accepted = await client.cashAcceptLocal(tenderedNum);
      if (change > 0) {
        await client.cashDispenseLocal(change);
      }
      const updated = await client.addTicketPayment(ticket.id, {
        payment_method_id: selectedMethod.id,
        amount: balance,
        tendered: accepted.accepted,
      });
      onPaid(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error en cobro automatizado');
    } finally {
      setSubmitting(false);
    }
  }

  async function payWithCardTerminal() {
    if (selectedMethod === null || !cardAvailable || !terminalId) return;
    setError(null);
    setSubmitting(true);
    try {
      // 1. Iniciar transaction en cloud (audit trail) — pending
      const tx = await client.startTerminalTransaction(terminalId, {
        kind: 'sale',
        amount: balance,
        ticket_id: ticket.id,
      });
      // 2. Mandar al Bridge local (datáfono físico, espera 5–60s al cliente)
      const result = await client.datafonoSaleLocal(Math.round(balance * 100), ticket.id);
      // 3. Cerrar transaction en cloud con la respuesta
      await client.finalizeTerminalTransaction(terminalId, tx.id, {
        approved: result.approved,
        auth_code: result.auth_code ?? undefined,
        rrn: result.rrn ?? undefined,
        card_brand: result.card_brand ?? undefined,
        card_last4: result.card_last4 ?? undefined,
        response_code: result.response_code ?? undefined,
        response_message: result.response_message ?? undefined,
        raw_response: { value: result.raw_response },
        amount: balance,
      });
      if (!result.approved) {
        setError(`Tarjeta DENEGADA: ${result.response_message ?? result.response_code ?? 'sin info'}`);
        return;
      }
      // 4. Recargar el ticket (el cloud creó el TicketPayment automáticamente)
      const updated = await client.getTicket(ticket.id);
      onPaid(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error en cobro con tarjeta');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-1 items-stretch overflow-hidden">
      <section className="flex flex-1 flex-col items-center justify-center gap-6 p-8">
        <div className="text-slate-400">A cobrar</div>
        <div className="font-mono text-7xl font-bold text-positec-accent">
          {balance.toFixed(2)}€
        </div>

        <div className="w-full max-w-md space-y-3">
          <div className="text-sm text-slate-400">Forma de pago</div>
          <div className="grid grid-cols-2 gap-2">
            {snapshot.payment_methods.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedMethod(m)}
                style={{ borderColor: selectedMethod?.id === m.id ? m.color ?? '#f97316' : undefined }}
                className={`rounded-lg border-2 px-3 py-3 text-base font-medium transition ${
                  selectedMethod?.id === m.id
                    ? 'bg-slate-800'
                    : 'border-slate-700 hover:bg-slate-800/50'
                }`}
              >
                {m.button_text ?? m.name}
              </button>
            ))}
          </div>

          {selectedMethod?.returns_change && (
            <>
              <div className="mt-4 text-sm text-slate-400">Entregado</div>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={tendered}
                onChange={(e) => setTendered(e.target.value)}
                className="w-full rounded border border-slate-700 bg-slate-800 px-3 py-3 text-2xl font-mono"
              />
              {change > 0 && (
                <div className="mt-2 text-right text-lg">
                  <span className="text-slate-400">Cambio:</span>{' '}
                  <span className="font-mono text-positec-accent">{change.toFixed(2)}€</span>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="rounded border border-red-700/50 bg-red-900/30 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}

          {cashStatus && (
            <div className="mt-2 rounded border border-slate-700 bg-slate-900/60 px-3 py-2 text-xs text-slate-300">
              Caja {cashStatus.online ? '🟢 online' : '🔴 offline'} · saldo {cashStatus.balance.toFixed(2)}€
              {cashStatus.alarms.length > 0 && (
                <div className="mt-1 text-amber-400">{cashStatus.alarms.join(' · ')}</div>
              )}
            </div>
          )}
        </div>

        <div className="flex w-full max-w-md flex-col gap-3">
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 rounded border border-slate-700 px-3 py-4 hover:bg-slate-800"
              disabled={submitting}
            >
              Volver
            </button>
            <button
              onClick={pay}
              disabled={submitting || selectedMethod === null || (selectedMethod.returns_change && tenderedNum < balance)}
              className="flex-[2] rounded bg-positec-accent px-3 py-4 text-lg font-semibold text-white transition hover:bg-orange-600 disabled:opacity-40"
            >
              {submitting ? 'Procesando…' : 'Cobrar'}
            </button>
          </div>
          {cashAvailable && (
            <button
              onClick={payWithCashMachine}
              disabled={submitting || tenderedNum < balance}
              className="rounded border border-emerald-600 bg-emerald-700/30 px-3 py-3 text-sm font-medium text-emerald-200 hover:bg-emerald-700/50 disabled:opacity-40"
            >
              💶 Cobrar con caja {cashStatus && `(saldo ${cashStatus.balance.toFixed(2)}€)`}
            </button>
          )}
          {cardAvailable && (
            <button
              onClick={payWithCardTerminal}
              disabled={submitting}
              className="rounded border border-sky-600 bg-sky-700/30 px-3 py-3 text-sm font-medium text-sky-200 hover:bg-sky-700/50 disabled:opacity-40"
            >
              💳 Cobrar con datáfono ({datafonoKind})
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
